/**
 * Shared pieces of the two reporter filing-history advisories: the Clear-all
 * confirm and the Report System Abuse warn interstitial (#3860), each shown
 * on both moderation surfaces (the legacy mod-action page and the cases
 * console). Single-sourced here so the copy, the numbers it cites, and the
 * policy claims inside it cannot drift between surfaces; the policy claims
 * themselves are pinned against their PHP constants by
 * tests/unit/Helpers/ReportAbuseWarnInterstitialMirrorTest.php.
 *
 * Consumed two ways, like moderation-action-submit.js: mod-cases.js imports
 * the ES exports, while the jQuery IIFE in moderate.js (bundled after this
 * file in the same webpack entry) reads window.ReportAbuseAdvisory.
 *
 * Sentences, not lines: ConfirmModal writes the message with textContent
 * into a <p> that has no white-space rule, so newlines would collapse to
 * spaces mid-sentence.
 */

/**
 * A hung filing-summary request must not strand the caller's re-entry latch:
 * the advisories set a pending flag before awaiting this lookup and clear it
 * in the modal callbacks, so a request that never settles would otherwise
 * leave the warn action dead until reload. Past this bound the lookup
 * resolves null and the advisory renders without the stats sentence.
 */
const FILING_SUMMARY_TIMEOUT_MS = 8000;

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/**
 * The trailing outcomes sentence both stat helpers share: how many of the
 * reporter's sampled targets are no longer active accounts.
 */
function inactiveTargetsSentence(summary) {
    const notActive = Number(summary.targets_not_active) || 0;
    const sampled = Number(summary.targets_sampled) || 0;
    if (sampled <= 0) return '';
    return `${notActive} of ${sampled} reported accounts (${pct(notActive, sampled)}%) are no longer active.`;
}

/**
 * The Clear-all advisory's stats sentence, citing the broad untyped count:
 * the clear action spans the reporter's whole history, so the wide number is
 * the honest one there.
 */
export function describeFilingHistory(summary) {
    if (!summary || !summary.total) return '';
    const untyped = Number(summary.untyped_reason_count) || 0;

    return [
        `${summary.total} report${summary.total === 1 ? '' : 's'} filed, ` +
        `${untyped} of them (${pct(untyped, summary.total)}%) with no typed reason.`,
        inactiveTargetsSentence(summary),
    ].filter(Boolean).join(' ');
}

/**
 * The report-abuse warn advisory's stats sentence. Cites the exactly-bare
 * Block + Report subset (`bare_block_count`), not the broader untyped count
 * describeFilingHistory renders: untyped also covers empty-reason ordinary
 * reports, which still reach the human queue, and this sentence sits
 * directly under a claim about automod-only routing, so citing the wider
 * number would overstate its own evidence.
 */
export function describeBareBlockFilings(summary) {
    if (!summary || !summary.total) return '';
    const bare = Number(summary.bare_block_count) || 0;

    return [
        `They filed ${summary.total} report${summary.total === 1 ? '' : 's'}. ` +
        `${bare} of them (${pct(bare, summary.total)}%) came from the Block and Report flow` +
        ' with nothing typed.',
        inactiveTargetsSentence(summary),
    ].filter(Boolean).join(' ');
}

/**
 * The reporter's filing summary, or null when it cannot be had: no ApiClient,
 * a failed request, or one still hanging past the timeout. A failed lookup
 * must never block the action the advisory decorates.
 */
export async function fetchFilingSummary(uid) {
    if (!window.ApiClient) return null;

    const lookup = (async () => {
        // limit=1 because only `summary` is read here. The endpoint always
        // describes the reporter's whole history regardless of page size.
        const response = await window.ApiClient.request(
            '/user/' + encodeURIComponent(uid) + '/reports?limit=1'
        );
        const payload = await response.json();
        return payload && payload.summary ? payload.summary : null;
    })().catch((err) => {
        console.warn('filing summary lookup:', err);
        return null;
    });
    const timeout = new Promise((resolve) => {
        setTimeout(resolve, FILING_SUMMARY_TIMEOUT_MS, null);
    });

    return Promise.race([lookup, timeout]);
}

/**
 * The warn interstitial's ConfirmModal options. The label undersells what
 * issuing the warning does: for the abuse-gate window the reporter abuse
 * gate changes how the warned user's reports resolve, and bare
 * block-and-report volume is automod-only signal (#3860), so volume alone is
 * not abuse. `leadSentence` lets the cases console name the subject; the
 * mod-action page omits it because the form sits on the subject's own page.
 */
export function reportAbuseWarnAdvisory(detailLine, leadSentence, onConfirm, onCancel) {
    const message = [
        leadSentence,
        'Heavy use of Block and Report is not report abuse on its own.',
        'Bare block-reports route to automod, not to the moderator queue.',
        detailLine,
        'Reserve this warning for malicious reporting, such as targeting clearly innocent users,'
            + ' coordinated pile-ons, or retaliation.',
        'It also changes how their future reports resolve: for the next 90 days,'
            + ' when automod clears the person they reported, the report is archived'
            + ' instead of reaching a moderator. Reports automod finds actionable are'
            + ' still acted on.',
    ].filter(Boolean).join(' ');

    return {
        title: 'Issuing a report-abuse warning?',
        message,
        confirmText: 'Issue warning anyway',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm,
        onCancel,
    };
}

// Global bridge for the jQuery IIFE consumer, mirroring ModActionSubmit.
window.ReportAbuseAdvisory = {
    describeFilingHistory,
    describeBareBlockFilings,
    fetchFilingSummary,
    reportAbuseWarnAdvisory,
};
